import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTachometerAlt,
  faUsers,
  faCalendarAlt,
  faFileAlt,
  faBookOpen,
  faImages,
  faCog,
  faBars,
  faSignOutAlt,
  faUserCog,
  faAngleDown,
  faChevronDown,
  faIdCard,
  faPercent,
  faUserTie,
  faAddressCard
} from '@fortawesome/free-solid-svg-icons';
import './AdminStyles.css';
import { AUTH_URLS } from '../../components/config/apiConfig';

function AdminLayout({ children }) {
  const [isSidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [isUserDropdownOpen, setUserDropdownOpen] = useState(false);
  const [adminName, setAdminName] = useState('');
  const [adminRole, setAdminRole] = useState('');
  
  const navigate = useNavigate();
  const location = useLocation();
  
  useEffect(() => {
    const storedName = localStorage.getItem('adminName');
    const storedRole = localStorage.getItem('adminRole');
    const token = localStorage.getItem('adminToken');
    const tokenExpires = localStorage.getItem('adminTokenExpires');
    const isTokenValid = token && tokenExpires && new Date(tokenExpires) > new Date();
    
    // Only redirect if not on login page and no valid session
    if ((!storedName || !isTokenValid) && location.pathname !== '/admin/login') {
      // Clear invalid admin data
      localStorage.removeItem('adminToken');
      localStorage.removeItem('adminId');
      localStorage.removeItem('adminName');
      localStorage.removeItem('adminEmail');
      localStorage.removeItem('adminIsSuperAdmin');
      localStorage.removeItem('adminIsViewOnly');
      localStorage.removeItem('adminTokenExpires');
      
      // Redirect to login if no admin session
      navigate('/admin/login');
    } else if (storedName) {
      setAdminName(storedName);
      setAdminRole(storedRole || 'regular');
    }
    
    // If you need to validate the token with the server:
    // const validateToken = async () => {
    //   try {
    //     const response = await fetch(`${AUTH_URLS.ADMIN_LOGIN}/validate`, {
    //       headers: {
    //         'Authorization': `Bearer ${token}`
    //       }
    //     });
    //     if (!response.ok) {
    //       handleInvalidSession();
    //     }
    //   } catch (error) {
    //     console.error('Error validating token:', error);
    //     handleInvalidSession();
    //   }
    // };
    // if (token) validateToken();
    
    // Close user dropdown when clicking outside
    const handleClickOutside = (event) => {
      if (!event.target.closest('.admin-user-menu') && isUserDropdownOpen) {
        setUserDropdownOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [navigate, isUserDropdownOpen, location.pathname]);
  
  const toggleSidebar = () => {
    setSidebarCollapsed(!isSidebarCollapsed);
  };
  
  const toggleUserDropdown = () => {
    setUserDropdownOpen(!isUserDropdownOpen);
  };
  
  const handleLogout = async () => {
    // You might want to call a logout endpoint to invalidate the token
    // try {
    //   await fetch(`${AUTH_URLS.ADMIN_LOGIN}/logout`, {
    //     method: 'POST',
    //     headers: {
    //       'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
    //     }
    //   });
    // } catch (error) {
    //   console.error('Error during logout:', error);
    // }
    
    // Clear admin session data
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminName');
    localStorage.removeItem('adminRole');
    
    // Redirect to login page
    navigate('/admin/login');
  };
  
  const isActive = (path) => {
    return location.pathname === path;
  };

  return (
    <div className="admin-layout">
      <div className={`admin-sidebar ${isSidebarCollapsed ? 'admin-sidebar-collapsed' : ''}`}>
        <div className="admin-logo-container">
          <Link to="/admin/dashboard" className="admin-logo">
            {isSidebarCollapsed ? 'BVA' : 'BVA Admin'}
          </Link>
        </div>
        
        <ul className="admin-menu">
          <li className="admin-menu-item">
            <Link 
              to="/admin/dashboard" 
              className={`admin-menu-link ${isActive('/admin/dashboard') ? 'active' : ''}`}
            >
              <FontAwesomeIcon icon={faTachometerAlt} className="admin-menu-icon" />
              {!isSidebarCollapsed && <span>Dashboard</span>}
            </Link>
          </li>
          
          <li className="admin-menu-item">
            <Link 
              to="/admin/members" 
              className={`admin-menu-link ${isActive('/admin/members') ? 'active' : ''}`}
            >
              <FontAwesomeIcon icon={faUsers} className="admin-menu-icon" />
              {!isSidebarCollapsed && <span>Members</span>}
            </Link>
          </li>
          
          <li className="admin-menu-item">
            <Link 
              to="/admin/membership-plans" 
              className={`admin-menu-link ${isActive('/admin/membership-plans') ? 'active' : ''}`}
            >
              <FontAwesomeIcon icon={faIdCard} className="admin-menu-icon" />
              {!isSidebarCollapsed && <span>Membership Plans</span>}
            </Link>
          </li>
          
          <li className="admin-menu-item">
            <Link 
              to="/admin/programs" 
              className={`admin-menu-link ${isActive('/admin/programs') ? 'active' : ''}`}
            >
              <FontAwesomeIcon icon={faCalendarAlt} className="admin-menu-icon" />
              {!isSidebarCollapsed && <span>Programs</span>}
            </Link>
          </li>
          
          <li className="admin-menu-item">
            <Link 
              to="/admin/office-bearers" 
              className={`admin-menu-link ${isActive('/admin/office-bearers') ? 'active' : ''}`}
            >
              <FontAwesomeIcon icon={faUserTie} className="admin-menu-icon" />
              {!isSidebarCollapsed && <span>Office Bearers</span>}
            </Link>
          </li>
          
          <li className="admin-menu-item">
            <Link 
              to="/admin/resources" 
              className={`admin-menu-link ${isActive('/admin/resources') ? 'active' : ''}`}
            >
              <FontAwesomeIcon icon={faBookOpen} className="admin-menu-icon" />
              {!isSidebarCollapsed && <span>Resources</span>}
            </Link>
          </li>
          
          <li className="admin-menu-item">
            <Link 
              to="/admin/payments" 
              className={`admin-menu-link ${isActive('/admin/payments') ? 'active' : ''}`}
            >
              <FontAwesomeIcon icon={faFileAlt} className="admin-menu-icon" />
              {!isSidebarCollapsed && <span>Payments</span>}
            </Link>
          </li>
          
          <li className="admin-menu-item">
            <Link 
              to="/admin/discounts" 
              className={`admin-menu-link ${isActive('/admin/discounts') ? 'active' : ''}`}
            >
              <FontAwesomeIcon icon={faPercent} className="admin-menu-icon" />
              {!isSidebarCollapsed && <span>Discounts</span>}
            </Link>
          </li>
          
          <li className="admin-menu-item">
            <Link 
              to="/admin/professional-listings" 
              className={`admin-menu-link ${isActive('/admin/professional-listings') || isActive('/admin/professional-listing-pricing') ? 'active' : ''}`}
            >
              <FontAwesomeIcon icon={faAddressCard} className="admin-menu-icon" />
              {!isSidebarCollapsed && <span>Professional Listings</span>}
            </Link>
          </li>
          
          <li className="admin-menu-item">
            <Link 
              to="/admin/gallery" 
              className={`admin-menu-link ${isActive('/admin/gallery') ? 'active' : ''}`}
            >
              <FontAwesomeIcon icon={faImages} className="admin-menu-icon" />
              {!isSidebarCollapsed && <span>Gallery</span>}
            </Link>
          </li>
          
          {adminRole === 'super' && (
            <li className="admin-menu-item">
              <Link 
                to="/admin/administrators" 
                className={`admin-menu-link ${isActive('/admin/administrators') ? 'active' : ''}`}
              >
                <FontAwesomeIcon icon={faUserCog} className="admin-menu-icon" />
                {!isSidebarCollapsed && <span>Administrators</span>}
              </Link>
            </li>
          )}
          
          <li className="admin-menu-item">
            <Link 
              to="/admin/settings" 
              className={`admin-menu-link ${isActive('/admin/settings') ? 'active' : ''}`}
            >
              <FontAwesomeIcon icon={faCog} className="admin-menu-icon" />
              {!isSidebarCollapsed && <span>Settings</span>}
            </Link>
          </li>
        </ul>
      </div>
      
      <div className={`admin-content ${isSidebarCollapsed ? 'admin-content-expanded' : ''}`}>
        <header className="admin-header">
          <div className="admin-header-left">
            <button className="admin-toggle-sidebar" onClick={toggleSidebar}>
              <FontAwesomeIcon icon={faBars} />
            </button>
            <h1 className="admin-header-title">Bangalore Valuers' Association Admin</h1>
          </div>
          
          <div className="admin-user-menu">
            <button className="admin-user-btn" onClick={toggleUserDropdown}>
              <span className="admin-user-name">{adminName}</span>
              <FontAwesomeIcon icon={faChevronDown} />
            </button>
            
            <div className={`admin-dropdown ${isUserDropdownOpen ? 'active' : ''}`}>
              <Link to="/admin/profile" className="admin-dropdown-item">
                Profile
              </Link>
              <button className="admin-dropdown-item" onClick={handleLogout} style={{ width: '100%', textAlign: 'left' }}>
                <FontAwesomeIcon icon={faSignOutAlt} style={{ marginRight: '0.5rem' }} />
                Logout
              </button>
            </div>
          </div>
        </header>
        
        <main className="admin-main">
          {children}
        </main>
      </div>
    </div>
  );
}

export default AdminLayout;
